import { useEffect, useState, useMemo } from "react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  MenuItem, TextField, InputAdornment, Avatar, AvatarGroup, Chip, Stack,
  Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Tooltip,
} from "@mui/material";
import DataGrid from './ui/DataTable';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import StatusDonut from './ui/StatusDonut';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';

import axiosInstance from './axiosInstance';
import { useSnackbar } from "../providers/SnackbarProvider";
import { useBreadcrumbs } from './BreadcrumbBar';
import SectionCard from './ui/SectionCard';
import EmptyState from './ui/EmptyState';
import { usePipelineConfig } from '../providers/PipelineConfigProvider';
import { phaseChartColors, phaseChipColors } from '../utils/phaseColors';


const initials = (name) =>
  (name || '?').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
  return isNaN(d) ? null : d;
};

const EMPTY_PROJECT = {
  name: '', account: '', status: 'Planning',
  start_date: dayjs().format('YYYY-MM-DD'),
  end_date: dayjs().add(3, 'month').format('YYYY-MM-DD'),
  description: '',
};

// Ein Projekt gilt als „laufend" (Endtermin zählt), solange es nicht abgeschlossen/abgebrochen ist.
const isOngoing = (status) => status !== 'Completed' && status !== 'Cancelled';

function ProjectList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  // Pipeline-Konfiguration (B3): Phasen & Labels aus dem Admin
  const { phasesFor, lostFor, statusLabelsFor } = usePipelineConfig();
  const projectPhases = phasesFor('project');
  const projectLost = lostFor('project');
  const STATUS_LABELS = statusLabelsFor('project');
  const allStatuses = [...projectPhases, ...(projectLost ? [projectLost] : [])];

  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newProject, setNewProject] = useState(EMPTY_PROJECT);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useBreadcrumbs([{ label: 'Projekte' }]);

  useEffect(() => { fetchProjects(); fetchAccounts(); }, []);

  const fetchProjects = () => {
    axiosInstance.get('/projects/')
      .then((res) => setProjects(res.data))
      .catch((err) => { console.error('Error fetching projects:', err); showSnackbar('Fehler beim Laden der Projekte', 'error'); })
      .finally(() => setLoading(false));
  };

  const fetchAccounts = async () => {
    try { const res = await axiosInstance.get('/accounts/'); setAccounts(res.data); }
    catch (err) { console.error('Error fetching accounts:', err); }
  };

  const handleClose = () => { setOpen(false); setNewProject(EMPTY_PROJECT); };

  const handleCreate = async () => {
    if (!newProject.name || !newProject.account) {
      showSnackbar('Name und Unternehmen sind erforderlich', 'error');
      return;
    }
    if (dayjs(newProject.end_date).isBefore(dayjs(newProject.start_date))) {
      showSnackbar('Das Enddatum muss nach dem Startdatum liegen', 'error');
      return;
    }
    try {
      const res = await axiosInstance.post('/projects/', newProject);
      setProjects([res.data, ...projects]);
      handleClose();
      showSnackbar('Projekt erfolgreich erstellt', 'success');
    } catch (err) {
      console.error('Error creating project:', err);
      showSnackbar(`Fehler beim Erstellen: ${err.response?.data?.detail || err.message}`, 'error');
    }
  };

  // ---------- Farben & Formatierung ----------
  // Design 3.0: Projekte & Zeit tragen die Blau-Ramp (zweite Farbfamilie)
  const chartColors = phaseChartColors(theme, projectPhases, projectLost, 'support');
  const chipColors = phaseChipColors(theme, projectPhases, projectLost, 'support');
  const statusColor = (s) => chartColors[s] || theme.palette.tokens.chartNeutral;
  const chipSx = (color) => ({ bgcolor: alpha(color, 0.13), color, fontWeight: 600 });

  const num = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('de-DE'));
  const timeProgress = (start, end) => {
    const total = dayjs(end).diff(dayjs(start), 'day');
    if (!total || total <= 0) return dayjs().isAfter(dayjs(end)) ? 100 : 0;
    const elapsed = dayjs().diff(dayjs(start), 'day');
    return Math.max(0, Math.min(100, (elapsed / total) * 100));
  };

  // ---------- Kennzahlen (clientseitig) ----------
  const metrics = useMemo(() => {
    const counts = projects.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});
    const statusDistribution = allStatuses
      .filter((o) => counts[o.key])
      .map((o) => ({ id: o.key, label: o.label, value: counts[o.key], color: statusColor(o.key) }));

    const active = counts['In Progress'] || 0;

    const ongoing = projects.filter((p) => isOngoing(p.status) && p.end_date);
    const overdue = ongoing.filter((p) => dayjs(p.end_date).isBefore(dayjs(), 'day')).length;
    const nearest = ongoing
      .filter((p) => !dayjs(p.end_date).isBefore(dayjs(), 'day'))
      .sort((a, b) => new Date(a.end_date) - new Date(b.end_date))[0] || null;

    return { statusDistribution, active, overdue, nearest };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // ---------- Filter ----------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (statusFilter !== 'ALL' && p.status !== statusFilter) return false;
      if (!q) return true;
      return [p.name, p.account_name].some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [projects, search, statusFilter]);

  // „Enddatum" mit Warnfärbung: überfällig rot, bald fällig (≤14 T.) gelb — nur solange laufend.
  const renderEndDate = (params) => {
    const { row } = params;
    if (!row.end_date) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const d = daysUntil(row.end_date);
    const ongoing = isOngoing(row.status);
    let color = 'text.secondary', weight = 400, note = '';
    if (ongoing && d !== null) {
      if (d < 0) { color = 'error.main'; weight = 700; note = `vor ${Math.abs(d)} ${Math.abs(d) === 1 ? 'Tag' : 'Tagen'}`; }
      else if (d <= 14) { color = 'warning.main'; weight = 700; note = d === 0 ? 'heute' : `in ${d} ${d === 1 ? 'Tag' : 'Tagen'}`; }
    }
    return (
      <Box sx={{ lineHeight: 1.25 }}>
        <Typography variant="body2" sx={{ color, fontWeight: weight, fontVariantNumeric: 'tabular-nums' }}>
          {dayjs(row.end_date).format('DD.MM.YYYY')}
        </Typography>
        {note && <Typography variant="caption" sx={{ color, display: 'block' }}>{note}</Typography>}
      </Box>
    );
  };

  const columns = [
    {
      field: 'name', headerName: 'Projekt', flex: 1.6, minWidth: 230,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', color: 'primary.dark', fontSize: '0.8rem', fontWeight: 700, flex: 'none' }}>
            {initials(params.row.name)}
          </Avatar>
          <Box sx={{ minWidth: 0, lineHeight: 1.3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{params.row.name}</Typography>
            {params.row.account_name && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{params.row.account_name}</Typography>}
          </Box>
        </Stack>
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 140,
      renderCell: (params) => params.row.status
        ? <Chip size="small" label={STATUS_LABELS[params.row.status] || params.row.status} sx={chipSx(chipColors[params.row.status] || theme.palette.tokens.chartNeutral)} />
        : <Box sx={{ color: 'text.disabled' }}>—</Box>,
    },
    {
      field: 'consultants', headerName: 'Berater', width: 150, sortable: false,
      renderCell: (params) => {
        const list = params.row.consultants || [];
        if (!list.length) return <Typography variant="caption" color="text.disabled">—</Typography>;
        return (
          <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 30, height: 30, fontSize: '0.72rem', fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark', borderColor: 'background.paper' }, justifyContent: 'flex-start' }}>
            {list.map((c, i) => (
              <Tooltip key={i} title={c} arrow placement="top"><Avatar>{initials(c)}</Avatar></Tooltip>
            ))}
          </AvatarGroup>
        );
      },
    },
    {
      field: 'contract_count', headerName: 'Verträge', width: 95, align: 'center', headerAlign: 'center',
      renderCell: (params) => <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{params.row.contract_count ?? 0}</Typography>,
    },
    {
      field: 'remaining_hours', headerName: 'Stunden (verf. / Budget)', width: 170,
      valueGetter: (value) => (value === null || value === undefined ? -Infinity : Number(value)),
      renderCell: (params) => {
        const rem = params.row.remaining_hours;
        const budget = params.row.hour_budget;
        const over = rem !== null && rem !== undefined && Number(rem) < 0;
        return (
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: over ? 'error.main' : 'text.primary', fontWeight: over ? 700 : 400 }}>
            {num(rem)} <Box component="span" sx={{ color: 'text.disabled' }}>/ {num(budget)}</Box>
          </Typography>
        );
      },
    },
    {
      field: 'end_date', headerName: 'Enddatum', width: 130,
      valueGetter: (value) => (value ? new Date(value).getTime() : 0),
      renderCell: renderEndDate,
    },
    {
      field: 'progress', headerName: 'Verlauf', width: 150, sortable: false,
      renderCell: (params) => {
        const p = timeProgress(params.row.start_date, params.row.end_date);
        const done = params.row.status === 'Completed';
        const bar = done ? theme.palette.success.main : p >= 100 ? theme.palette.error.main : theme.palette.primary.main;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <LinearProgress variant="determinate" value={done ? 100 : p}
              sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.08), '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: bar } }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 30, textAlign: 'right' }}>
              {Math.round(done ? 100 : p)}%
            </Typography>
          </Box>
        );
      },
    },
  ];

  const filtersActive = search || statusFilter !== 'ALL';

  // Leerzustände (D4): erste Nutzung vs. leeres Such-/Filterergebnis
  const NoRowsOverlay = () => (filtersActive
    ? <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
    : (
      <EmptyState
        title="Noch keine Projekte."
        hint="Das erste entsteht meist aus einem gewonnenen Lead."
        actionLabel="Neues Projekt"
        onAction={() => setOpen(true)}
      />
    ));
  const NoResultsOverlay = () => (
    <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
  );
  const statusFilters = [{ key: 'ALL', label: 'Alle' }, ...allStatuses.map((o) => ({ key: o.key, label: o.label }))];

  const tile = (children) => (
    <SectionCard sx={{ height: 180, display: 'flex', flexDirection: 'column' }}>{children}</SectionCard>
  );

  return (
    <Box>
      <Grid container spacing={2.5}>
        {/* Kopf */}
        <Grid size={12}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
            <Box>
              <Typography variant="h4" component="h1">Projekte</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {filtered.length} {filtered.length === 1 ? 'Projekt' : 'Projekte'}{filtersActive ? ` (von ${projects.length})` : ''}
              </Typography>
            </Box>
            <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ alignSelf: { xs: 'flex-start', md: 'auto' } }}>
              Neues Projekt
            </Button>
          </Stack>
        </Grid>

        {/* Kennzahlen */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>Statusverteilung</Typography>
              {metrics.statusDistribution.length ? (
                <StatusDonut
                  data={metrics.statusDistribution}
                  centerLabel="Projekte"
                  onSliceClick={(id) => setStatusFilter(id)}
                />
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.disabled">Keine Projekte</Typography>
                </Box>
              )}
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>
                Aktive Projekte{metrics.active ? ` · ${metrics.active}` : ''}
              </Typography>
              {/* Je aktives Projekt: Zeitfortschritt + Tage bis Ende — „welches brennt?" */}
              {metrics.active ? (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                  {projects
                    .filter((p) => p.status === 'In Progress')
                    .sort((a, b) => new Date(a.end_date) - new Date(b.end_date))
                    .slice(0, 3)
                    .map((p) => {
                      const daysLeft = dayjs(p.end_date).startOf('day').diff(dayjs().startOf('day'), 'day');
                      const overdue = daysLeft < 0;
                      return (
                        <Box key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                          sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: '0.76rem' }}>
                            <Typography component="span" noWrap sx={{ fontSize: 'inherit', fontWeight: 600, minWidth: 0 }}>{p.name}</Typography>
                            <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 650, flex: 'none', fontVariantNumeric: 'tabular-nums', color: overdue ? 'error.main' : 'text.secondary' }}>
                              {overdue ? 'überfällig' : daysLeft === 0 ? 'heute' : `${daysLeft} Tg.`}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 6, borderRadius: 3, bgcolor: theme.palette.tokens.ramp2[100], mt: 0.4 }}>
                            <Box sx={{ height: 6, borderRadius: 3, width: `${timeProgress(p.start_date, p.end_date)}%`, bgcolor: overdue ? 'error.main' : theme.palette.tokens.ramp2[500] }} />
                          </Box>
                        </Box>
                      );
                    })}
                  {metrics.active > 3 && (
                    <Typography variant="caption" color="text.secondary">+ {metrics.active - 3} weitere</Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.disabled">Keine aktiven Projekte</Typography>
                </Box>
              )}
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>Überfällig</Typography>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: metrics.overdue ? 'error.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{metrics.overdue}</Typography>
                <Typography variant="body2" color="text.secondary">Endtermin überschritten</Typography>
              </Box>
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>Nächstes Enddatum</Typography>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                {metrics.nearest ? (
                  <>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{dayjs(metrics.nearest.end_date).format('DD.MM.YYYY')}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5, maxWidth: '100%' }}>{metrics.nearest.name}</Typography>
                    {metrics.nearest.account_name && <Typography variant="caption" color="text.secondary" noWrap>{metrics.nearest.account_name}</Typography>}
                  </>
                ) : (
                  <Typography variant="body2" color="text.disabled">Keine offenen Termine</Typography>
                )}
              </Box>
            </>
          )}
        </Grid>

      </Grid>

      {/* Suche + stille Status-Filter (block-level: verhindert 0-Breite der DataGrid beim Mount) */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap" useFlexGap sx={{ mt: 2.5, mb: 2 }}>
            <TextField
              size="small" placeholder="Suchen …" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240, maxWidth: 360, bgcolor: 'background.paper' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment> }}
            />
            <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
              {statusFilters.map((f) => {
                const active = statusFilter === f.key;
                return (
                  <Box
                    key={f.key} role="button" tabIndex={0}
                    onClick={() => setStatusFilter(f.key)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStatusFilter(f.key); } }}
                    sx={{
                      fontSize: '0.8rem', fontWeight: 600, px: active ? 1.25 : 0.75, py: 0.3, borderRadius: 999,
                      cursor: 'pointer', userSelect: 'none',
                      color: active ? 'primary.main' : 'text.disabled',
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      '&:hover': { color: active ? 'primary.main' : 'text.secondary' },
                      '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
                    }}
                  >
                    {f.label}
                  </Box>
                );
              })}
            </Box>
          </Stack>

      {/* Tabelle als Fläche */}
      <SectionCard sx={{ p: 0, overflow: 'hidden' }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              getRowId={(row) => row.id}
              onRowClick={(params) => navigate(`/projects/${params.row.id}`)}
              loading={loading}
              disableRowSelectionOnClick
              autoHeight
              rowHeight={56}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay, noResultsOverlay: NoResultsOverlay }}
              sx={{
                border: 'none',
                // Platz für den Leerzustand inkl. Aktion (Default: 2 Zeilenhöhen)
                '--DataGrid-overlayHeight': '180px',
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
              }}
            />
      </SectionCard>

      {/* Dialog: Neues Projekt */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Neues Projekt erstellen</DialogTitle>
        <DialogContent>
          <Typography variant="overline" color="text.secondary">Projekt</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} fullWidth required autoFocus />
            <TextField select label="Unternehmen" value={newProject.account} onChange={(e) => setNewProject({ ...newProject, account: e.target.value })} fullWidth required>
              {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </TextField>
            <TextField select label="Status" value={newProject.status} onChange={(e) => setNewProject({ ...newProject, status: e.target.value })} fullWidth>
              {allStatuses.map((o) => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
            </TextField>
          </Box>

          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Zeitraum</Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField label="Start" type="date" value={newProject.start_date} onChange={(e) => setNewProject({ ...newProject, start_date: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Ende" type="date" value={newProject.end_date} onChange={(e) => setNewProject({ ...newProject, end_date: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
          </Box>

          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Beschreibung</Typography>
          <Box sx={{ mt: 1 }}>
            <TextField value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} multiline rows={3} fullWidth placeholder="Optionale Notizen zum Projekt …" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!newProject.name || !newProject.account}>Erstellen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectList;
